import { Lightning } from '@lightningjs/sdk'
import { getColor, grey960, transparent } from '../../constants/colors'
import { LEFT_SAFE_ZONE_MARGIN, WIDGETS_Z_INDEX } from '../../constants/general'
import { ArrowButton, Direction } from './ArrowButton'

export const enum PointerOrientation {
  VERTICAL = 'VERTICAL',
  HORIZONTAL = 'HORIZONTAL',
}

export const enum VerticalPointerSize {
  STANDARD = 'STANDARD',
  SMALL = 'SMALL',
  MINI = 'MINI',
}

const enum VerticalPointerHeight {
  STANDARD = 200,
  SMALL = 140,
  MINI = 65,
}

export const enum HorizontalPointerWidth {
  LEFT = 69,
  RIGHT = 166,
}

const POINTER_WIDTH = 1920 - LEFT_SAFE_ZONE_MARGIN
export const NAVIGATION_POINTERS_Z_INDEX = 5

export class NavigationPointers extends Lightning.Component {
  private _verticalPointerSize: VerticalPointerSize

  static _template() {
    return {
      visible: false,
      flexItem: false,
      zIndex: NAVIGATION_POINTERS_Z_INDEX,
      w: POINTER_WIDTH,
      h: 1080,
      flex: {
        direction: 'column',
        justifyContent: 'space-between',
        paddingLeft: LEFT_SAFE_ZONE_MARGIN,
      },

      TopContainer: {
        visible: false,
        collision: true,
        // zIndex must be higher than the root's zIndex for this component, otherwise, the event will go down to the card tiles
        zIndex: NAVIGATION_POINTERS_Z_INDEX + 1,
        w: POINTER_WIDTH,
        h: VerticalPointerHeight.STANDARD,

        TopPointer: {
          w: (w) => w,
          h: (h) => h,

          TopGradient: {
            h: (h) => h,
            w: (w) => w,
            flex: {
              direction: 'column',
            },
          },

          ArrowUp: {
            mountX: 0.5,
            mountY: 1,
            x: (w) => w / 2,
            y: (h) => h,
            type: ArrowButton,
            direction: Direction.Up,
          },

          TopOverflowCollisionBox: {
            w: (w) => w,
            y: (h) => h,
            collision: true,
            zIndex: NAVIGATION_POINTERS_Z_INDEX + 1,
          },
        },
      },

      BottomContainer: {
        visible: false,
        collision: true,
        zIndex: NAVIGATION_POINTERS_Z_INDEX + 1,
        w: POINTER_WIDTH,
        h: VerticalPointerHeight.STANDARD,

        BottomPointer: {
          w: (w) => w,
          h: (h) => h,

          BottomGradient: {
            h: (h) => h,
            w: (w) => w,
            flex: {
              direction: 'column',
            },
          },

          ArrowDown: {
            mountX: 0.5,
            mountY: 0,
            x: (w) => w / 2,
            y: 0,
            type: ArrowButton,
            direction: Direction.Down,
          },

          BottomOverflowCollisionBox: {
            w: (w) => w,
            mountY: 1,
            y: 0,
            collision: true,
            zIndex: NAVIGATION_POINTERS_Z_INDEX + 1,
          },
        },
      },

      LeftContainer: {
        visible: false,
        collision: true,
        zIndex: NAVIGATION_POINTERS_Z_INDEX + 1,
        w: HorizontalPointerWidth.LEFT,
        h: (h) => h,

        LeftPointer: {
          w: (w) => w,
          h: (h) => h,

          LeftGradient: {
            h: (h) => h,
            w: (w) => w,
            flex: {
              direction: 'row',
            },
          },

          ArrowLeft: {
            //zIndex must be higher than the main menu, otherwise, user can't click on the whole arrow because of the overlap
            zIndex: WIDGETS_Z_INDEX + 1,
            mountX: 0.5,
            mountY: 0.5,
            x: -5,
            y: (h) => h / 2,
            type: ArrowButton,
            direction: Direction.Left,
          },
        },
      },

      RightContainer: {
        visible: false,
        collision: true,
        zIndex: NAVIGATION_POINTERS_Z_INDEX + 1,
        w: HorizontalPointerWidth.RIGHT,
        h: (h) => h,

        RightPointer: {
          w: (w) => w,
          h: (h) => h,

          RightGradient: {
            h: (h) => h,
            w: (w) => w,
            flex: {
              direction: 'row',
            },
          },

          ArrowRight: {
            mountX: 0.5,
            mountY: 0.5,
            x: (w) => w / 2,
            y: (h) => h / 2,
            type: ArrowButton,
            direction: Direction.Right,
          },
        },
      },
    }
  }

  get topContainer() {
    return this.tag('TopContainer')
  }

  get topPointer() {
    return this.tag('TopPointer')
  }

  get topGradient() {
    return this.tag('TopGradient')
  }

  get arrowUp() {
    return this.tag('ArrowUp') as ArrowButton
  }

  get topOverflowCollisionBox() {
    return this.tag('TopOverflowCollisionBox')
  }

  get bottomContainer() {
    return this.tag('BottomContainer')
  }

  get bottomPointer() {
    return this.tag('BottomPointer')
  }

  get bottomGradient() {
    return this.tag('BottomGradient')
  }

  get arrowDown() {
    return this.tag('ArrowDown') as ArrowButton
  }

  get bottomOverflowCollisionBox() {
    return this.tag('BottomOverflowCollisionBox')
  }

  get leftContainer() {
    return this.tag('LeftContainer')
  }

  get leftPointer() {
    return this.tag('LeftPointer')
  }

  get leftGradient() {
    return this.tag('LeftGradient')
  }

  get arrowLeft() {
    return this.tag('ArrowLeft') as ArrowButton
  }

  get rightContainer() {
    return this.tag('RightContainer')
  }

  get rightPointer() {
    return this.tag('RightPointer')
  }

  get rightGradient() {
    return this.tag('RightGradient')
  }

  get arrowRight() {
    return this.tag('ArrowRight') as ArrowButton
  }

  set showTopPointer(show: boolean) {
    this.topPointer.patch({
      visible: show,
    })
  }

  set showBottomPointer(show: boolean) {
    this.bottomPointer.patch({
      visible: show,
    })
  }

  set showLeftPointer(show: boolean) {
    this.leftPointer.patch({
      visible: show,
    })
  }

  set showRightPointer(show: boolean) {
    this.rightPointer.patch({
      visible: show,
    })
  }

  set pointerOrientation(orientation: PointerOrientation) {
    this.patch({
      flex: {
        direction: orientation === PointerOrientation.VERTICAL ? 'column' : 'row',
        paddingLeft: orientation === PointerOrientation.VERTICAL ? LEFT_SAFE_ZONE_MARGIN : 0,
      },
    })
    this.topContainer.patch({
      visible: orientation === PointerOrientation.VERTICAL,
    })
    this.bottomContainer.patch({
      visible: orientation === PointerOrientation.VERTICAL,
    })
    this.leftContainer.patch({
      visible: orientation !== PointerOrientation.VERTICAL,
    })
    this.rightContainer.patch({
      visible: orientation !== PointerOrientation.VERTICAL,
    })
  }

  set pointerWidth(v: number) {
    this.patch({
      w: v,
      flex: {
        paddingLeft: 1920 - v,
      },
    })
    this.topContainer.patch({
      w: v,
    })
    this.bottomContainer.patch({
      w: v,
    })
    this.renderGradient()
  }

  set paddingLeft(v: number) {
    this.patch({
      flex: {
        paddingLeft: v,
      },
    })
    this.renderGradient()
  }

  get verticalPointerSize() {
    return this._verticalPointerSize
  }

  set verticalPointerSize(v: VerticalPointerSize) {
    this._verticalPointerSize = v
    let height = VerticalPointerHeight.STANDARD

    if (v === VerticalPointerSize.SMALL) {
      height = VerticalPointerHeight.SMALL
    } else if (v === VerticalPointerSize.MINI) {
      height = VerticalPointerHeight.MINI
    }

    this.topContainer.patch({
      h: height,
    })
    this.arrowUp.patch({
      y: height + (v === VerticalPointerSize.MINI ? 25 : 0),
    })

    this.bottomContainer.patch({
      h: height,
    })
    this.arrowDown.patch({
      y: v === VerticalPointerSize.MINI ? -25 : 0,
    })

    this.renderGradient()
  }

  _init() {
    this.renderGradient()
  }

  renderGradient() {
    const isMini = this._verticalPointerSize === VerticalPointerSize.MINI

    const verticalGradient = [
      {
        rect: true,
        h: (h) => h * (isMini ? 0 : 0.0366),
        w: (w) => w,
        colorTop: transparent,
        colorBottom: getColor(grey960, 0.0001),
      },
      {
        rect: true,
        h: (h) => h * (isMini ? 0.5 : 0.1843),
        w: (w) => w,
        colorTop: getColor(grey960, 0.0001),
        colorBottom: getColor(grey960, 0.7),
      },
      {
        rect: true,
        h: (h) => h * (isMini ? 0.5 : 0.7791),
        w: (w) => w,
        color: getColor(grey960, 0.7),
      },
    ]

    this.topGradient.patch({
      children: verticalGradient,
      rotation: Math.PI,
    })
    this.bottomGradient.patch({
      children: verticalGradient,
    })

    const leftOverflowGradient = {
      flexItem: false,
      rect: true,
      mountX: 1,
      x: 0,
      h: (h) => h,
      w: HorizontalPointerWidth.RIGHT - HorizontalPointerWidth.LEFT,
      color: getColor(grey960, 0.7),
      collision: true,
    }
    const horizontalGradient = [
      {
        rect: true,
        h: (h) => h,
        w: (w) => w * 0.5628,
        color: getColor(grey960, 0.7),
      },
      {
        rect: true,
        h: (h) => h,
        w: (w) => w * 0.4372,
        colorLeft: getColor(grey960, 0.7),
        colorRight: getColor(grey960, 0.0001),
      },
    ]

    this.leftGradient.patch({
      children: [leftOverflowGradient, ...horizontalGradient],
    })
    this.rightGradient.patch({
      children: horizontalGradient,
      rotation: Math.PI,
    })
  }
}
